<template>
  <!-- Only render when `internalShow` is true -->
  <div v-if="internalShow" :class="alertClasses" role="alert">
    <slot />

    <!-- Close button when dismissible -->
    <button
      v-if="dismissible"
      type="button"
      class="close"
      aria-label="Close"
      @click="onClose"
    >
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
</template>

<script>
export default {
  name: "BaseAlert",
  // Allow `v-model="show"` shorthand
  model: {
    prop: "show",
    event: "input",
  },
  props: {
    /** Visible state: boolean or number of seconds to auto-dismiss */
    show: { type: [Boolean, Number], default: false },
    /** Bootstrap contextual variant */
    variant: { type: String, default: "info" },
    /** Show the little × button */
    dismissible: { type: Boolean, default: false },
    /** Enable fade transition (adds .fade and .show) */
    fade: { type: Boolean, default: false },
  },
  data() {
    return {
      // For internal auto-dismiss timer
      timer: null,
    };
  },
  computed: {
    /** Compute the full list of Bootstrap classes */
    alertClasses() {
      const cls = ["alert", `alert-${this.variant}`];
      if (this.dismissible) cls.push("alert-dismissible");
      if (this.fade) {
        cls.push("fade");
        // Only add `.show` when actually visible
        if (this.internalShow) cls.push("show");
      }
      return cls;
    },
    /** Two-way binding on `show` */
    internalShow: {
      get() {
        // Coerce numeric -> boolean
        return (
          this.show === true || (typeof this.show === "number" && this.show > 0)
        );
      },
      set(val) {
        // Emit `input` so v-model works, and parent can update `show`
        this.$emit("input", val);
      },
    },
  },
  watch: {
    show(newVal) {
      this._startAutoDismiss(newVal);
    },
  },
  mounted() {
    // Kick off auto-dismiss if needed
    this._startAutoDismiss(this.show);
  },
  beforeDestroy() {
    if (this.timer) clearTimeout(this.timer);
  },
  methods: {
    _startAutoDismiss(val) {
      // Clear any existing timer
      if (this._timer) clearTimeout(this._timer);
      // If show is numeric > 0, dismiss after that many seconds
      if (typeof val === "number" && val > 0) {
        this.timer = setTimeout(() => {
          this.internalShow = false;
          this.$emit("dismissed");
        }, val * 1000);
      }
    },
    onClose() {
      this.internalShow = false;
      this.$emit("dismissed");
    },
  },
};
</script>
